import React, { useEffect, useState } from "react";
import { AlertTriangle, GitMerge, LoaderCircle, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

export default function CrewCleanupPreviewModal({ onClose }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    api.get("/api/admin/crm/crew/cleanup-preview")
      .then((response) => { if (active) setPreview(response.data); })
      .catch((error) => toast.error(error?.response?.data?.detail || "Could not build crew cleanup preview"))
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  const contactName = (contact) => contact?.stage_name || contact?.legal_name || "Unknown name";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-3 sm:p-6" onClick={onClose}>
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="crew-cleanup-title"
        className="flex max-h-[92vh] w-full max-w-5xl flex-col border"
        style={{ background: "var(--gio-surface-hi)", borderColor: "var(--gio-border)" }}
        onClick={(event) => event.stopPropagation()}
      >
        <header className="flex items-start justify-between gap-4 border-b p-5" style={{ borderColor: "var(--gio-border)" }}>
          <div>
            <div className="overline mb-1 inline-flex items-center gap-2"><GitMerge size={13} /> Read-only review</div>
            <h2 id="crew-cleanup-title" className="font-heading text-xl">Crew duplicate cleanup preview</h2>
            <p className="mt-1 text-sm" style={{ color: "var(--gio-muted)" }}>No records have been changed. Review every suggested group before approving any merge.</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close preview" className="p-1" style={{ color: "var(--gio-muted)" }}><X size={20} /></button>
        </header>

        <div className="flex-1 space-y-5 overflow-y-auto p-5">
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-16" style={{ color: "var(--gio-muted)" }}>
              <LoaderCircle className="animate-spin" size={18} /> Building preview
            </div>
          ) : preview ? (
            <>
              <div className="grid grid-cols-2 gap-px border sm:grid-cols-4" style={{ background: "var(--gio-border)", borderColor: "var(--gio-border)" }}>
                <PreviewStat label="Total current records" value={preview.total_current_records} />
                <PreviewStat label="Canonical after merge" value={preview.canonical_records_after_merge} />
                <PreviewStat label="Duplicate merge count" value={preview.duplicate_merge_count} />
                <PreviewStat label="Candidate groups" value={preview.groups_count} />
              </div>
              <div className="flex items-start gap-2 border p-3 text-sm" style={{ borderColor: "var(--gio-primary)", color: "var(--gio-text)" }}>
                <AlertTriangle className="mt-0.5 shrink-0" size={16} style={{ color: "var(--gio-primary)" }} />
                <span>Candidate groups are based on exact matching email, phone, or social handles within the same existing role. Similar names alone are never grouped. Nothing is merged from this screen.</span>
              </div>
              {preview.groups.length === 0 ? (
                <p className="py-8 text-center" style={{ color: "var(--gio-muted)" }}>No exact-identifier duplicate groups found.</p>
              ) : (
                <div className="space-y-3">
                  {preview.groups.map((group, index) => (
                    <article key={`${group.master.id}-${index}`} className="border p-4" style={{ borderColor: "var(--gio-border)", background: "var(--gio-surface-2)" }}>
                      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                        <h3 className="text-sm font-semibold">Group {index + 1}: {contactName(group.master)}</h3>
                        <span className="text-xs" style={{ color: "var(--gio-muted)" }}>Reason: {group.reason.join(", ") || "Exact identifier"}</span>
                      </div>
                      <ContactLine label="Kept master" contact={group.master} name={contactName(group.master)} />
                      {group.duplicates.map((contact) => (
                        <ContactLine key={contact.id} label="Suggested duplicate" contact={contact} name={contactName(contact)} />
                      ))}
                    </article>
                  ))}
                </div>
              )}
              {preview.groups_truncated && <p className="text-sm" style={{ color: "var(--gio-muted)" }}>Showing the first 30 of {preview.groups_count} groups.</p>}
            </>
          ) : (
            <p className="py-10 text-center" style={{ color: "var(--gio-muted)" }}>Preview unavailable. No records were changed.</p>
          )}
        </div>

        <footer className="flex justify-end border-t p-4" style={{ borderColor: "var(--gio-border)" }}>
          <button type="button" onClick={onClose} className="btn-ghost">Close preview</button>
        </footer>
      </section>
    </div>
  );
}

function PreviewStat({ label, value }) {
  return <div className="p-4" style={{ background: "var(--gio-surface-2)" }}>
    <div className="text-xs" style={{ color: "var(--gio-muted)" }}>{label}</div>
    <div className="mt-1 font-heading text-2xl" style={{ color: "var(--gio-primary)" }}>{value ?? "—"}</div>
  </div>;
}

function ContactLine({ label, contact, name }) {
  const details = [contact.type, contact.city, contact.country, contact.email, contact.phone].filter(Boolean);
  return <div className="flex flex-col gap-1 border-t py-2 sm:flex-row sm:items-center sm:justify-between" style={{ borderColor: "var(--gio-border)" }}>
    <div><span className="mr-2 text-[10px] uppercase" style={{ color: "var(--gio-primary)" }}>{label}</span><span className="text-sm">{name}</span></div>
    <span className="text-xs" style={{ color: "var(--gio-muted)" }}>{details.join(" · ")} · ID {contact.id}</span>
  </div>;
}